import { Stack } from '@/components/layout';
import { Skeleton } from '@/components/ui';
import type { EventDetailSurface } from '@/features/guardian/components/event-detail/event-detail-header';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

type EventDetailSkeletonProps = {
  surface?: EventDetailSurface;
};

export function EventDetailSkeleton({ surface = 'screen' }: EventDetailSkeletonProps) {
  const { scale } = useScaling();
  const { top } = useSafeAreaInsets();
  const styles = useStyles();

  return (
    <Stack flex={1} style={styles.root}>
      <Stack
        direction="row"
        align="center"
        gap={scale(12)}
        paddingX={scale({ phone: 20, tablet: 12 })}
        paddingTop={surface === 'screen' ? top + scale(8) : scale(12)}
        paddingBottom={scale(12)}>
        <Skeleton width={scale(40)} height={scale(40)} borderRadius={scale(20)} />
        <Skeleton width="40%" height={scale(20)} borderRadius={scale(6)} />
        <Stack flex={1} />
        <Skeleton width={scale(40)} height={scale(40)} borderRadius={scale(20)} />
      </Stack>
      <Stack
        flex={1}
        gap={scale(10)}
        padding={scale({ phone: 20, tablet: 12 })}
        paddingTop={surface === 'screen' ? scale(8) : scale(12)}>
        <Stack gap={scale(10)} style={styles.card}>
          <Skeleton.Line width="72%" height={scale(18)} />
          <Stack direction="row" gap={scale(8)}>
            <Skeleton width={scale(94)} height={scale(30)} borderRadius={scale(15)} />
            <Skeleton width={scale(88)} height={scale(30)} borderRadius={scale(15)} />
          </Stack>
        </Stack>
        <Stack gap={scale(18)} style={[styles.card, styles.infoCard]}>
          {[72, 88, 64, 78, 86, 94].map((width, index) => (
            <Stack key={`${width}-${index}`} direction="row" gap={scale(12)} align="center">
              <Skeleton width={scale(20)} height={scale(20)} borderRadius={scale(6)} />
              <Skeleton.Line width={`${width}%`} height={scale(14)} />
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  root: {
    backgroundColor: guardianColors['1'],
  },
  card: {
    padding: scale(12),
    borderRadius: scale(12),
    borderCurve: 'continuous',
    backgroundColor: colors['0'],
  },
  infoCard: {
    flex: 1,
  },
}));
